import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Minimize2, Calendar, Bot, User, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveChatWidget = ({ onOpenCalendar }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    company: '',
    employees: '50-100'
  });
  const messagesEndRef = useRef(null);
  
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: "Hi! 👋 I'm Zen1, your Zenova assistant. I'm here to help you understand how we can transform your business.",
      time: new Date()
    },
    {
      id: 2,
      type: 'bot',
      text: "I can help you with:\n• Understanding our services\n• The STRENGTH Framework\n• Pricing & ROI\n• Booking a free 7-min demo\n\nWhat would you like to know?",
      time: new Date()
    }
  ]);

  const quickReplies = [
    "What's the pricing?",
    "Book a demo",
    "STRENGTH Framework",
    "How does it work?"
  ];

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const addBotMessage = (text, delay = 1000) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const botMsg = {
        id: Date.now(),
        type: 'bot',
        text: text,
        time: new Date()
      };
      setMessages(prev => [...prev, botMsg]);
    }, delay);
  };

  const handleSend = (text = message) => {
    if (!text.trim()) return;

    // Add user message
    const userMsg = {
      id: Date.now(),
      type: 'user',
      text: text,
      time: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    setMessage('');

    const lowerText = text.toLowerCase();

    // Check for booking intent
    if (lowerText.includes('book') || lowerText.includes('demo') || lowerText.includes('session') || lowerText.includes('schedule') || lowerText.includes('appointment')) {
      addBotMessage("Great choice! 🎉 I can help you book a free 7-minute demo session right now.\n\nWould you like me to set that up for you?", 800);
      setTimeout(() => {
        setShowBookingForm(true);
      }, 1500);
      return;
    }

    // Handle other queries
    let response = '';

    if (lowerText.includes('price') || lowerText.includes('pricing') || lowerText.includes('cost') || lowerText.includes('fee')) {
      response = "Our complete 90-day transformation starts at ₹1,20,000/year for companies up to 300 employees.\n\nThis includes:\n✓ Business System Audit\n✓ SOP Creation & Training\n✓ Dashboard Setup\n✓ 3 Months Monitoring\n\nWould you like to book a demo to discuss your specific needs?";
    } else if (lowerText.includes('strength') || lowerText.includes('framework') || lowerText.includes('methodology')) {
      response = "The STRENGTH Framework™ is our proprietary 8-step methodology:\n\n🔍 S - Study (Map workflows)\n🔧 T - Tweak (Redesign SOPs)\n📚 R - Re-Train (Team training)\n📦 E - Embed (Implement)\n💻 N - Normalize (Digitize)\n📊 G - Gauge (KPIs & audits)\n✂️ T - Trim (Eliminate waste)\n🛡️ H - Harden (Backup systems)\n\nWant me to explain any step in detail?";
    } else if (lowerText.includes('how') || lowerText.includes('work') || lowerText.includes('process') || lowerText.includes('implement')) {
      response = "Our 90-day transformation journey:\n\n📋 Week 1-2: Study & Mapping\nDeep audit of your workflows and dependencies\n\n📝 Week 3-4: System Design\nSOPs, escalation matrices, role clarity\n\n💻 Week 5-8: Digital Embedding\nDashboards, tools, automation\n\n🔒 Week 9-12: Monitoring & Hardening\nAudits, KPI reviews, resilience building\n\nYou'll see results from Week 3! Want to get started?";
    } else if (lowerText.includes('sap') || lowerText.includes('erp') || lowerText.includes('software')) {
      response = "Great question! Unlike SAP which costs ₹50+ Lakhs and takes 12-24 months, Zenova:\n\n✓ Costs just ₹1.20 Lakhs/year (90% savings)\n✓ Implements in 90 days\n✓ Built specifically for Indian SMEs\n✓ India-based 24/7 support\n\nYou don't need complex enterprise software. You need systems that work for YOUR business size.";
    } else if (lowerText.includes('revenue') || lowerText.includes('5 cr') || lowerText.includes('crore') || lowerText.includes('company size')) {
      response = "We work best with companies that have:\n\n💰 ₹5 Crore+ annual revenue\n👥 50-500 employees\n📈 Ambition to 2x their growth\n🎯 Ready to delegate day-to-day operations\n\nIf this sounds like you, you're exactly who we built Zenova for! Want to explore how we can help you scale?";
    } else if (lowerText.includes('result') || lowerText.includes('roi') || lowerText.includes('return') || lowerText.includes('benefit')) {
      response = "Our clients typically see:\n\n📉 85% reduction in owner dependency\n⏰ 20+ hours/week saved from firefighting\n📈 2x revenue growth potential\n💰 ROI within first 90 days\n\nWant me to calculate your potential savings? Or book a demo to see real case studies?";
    } else if (lowerText.includes('contact') || lowerText.includes('talk') || lowerText.includes('call') || lowerText.includes('phone') || lowerText.includes('email')) {
      response = "You can reach us at:\n\n📧 Email: info@zenovagrowthpartners.com\n📱 Phone: +91 98765 43210\n💬 WhatsApp: Click the green button below\n\nOr I can book a demo for you right now! Would you like that?";
    } else if (lowerText.includes('thank') || lowerText.includes('thanks') || lowerText.includes('bye') || lowerText.includes('okay') || lowerText.includes('ok')) {
      response = "You're welcome! 😊 Feel free to ask me anything else about Zenova.\n\nRemember, I can also book a free 7-minute demo for you whenever you're ready. Just say 'book a demo'!";
    } else if (lowerText.includes('hi') || lowerText.includes('hello') || lowerText.includes('hey')) {
      response = "Hello! 👋 Great to connect with you!\n\nI'm Zen1, and I'm here to help you learn how Zenova can transform your business from chaos to control.\n\nWhat would you like to know about?";
    } else {
      response = "Thanks for your question! I'd love to help you better.\n\nHere's what I can assist with:\n• Pricing & packages\n• STRENGTH Framework methodology\n• Implementation process\n• SAP comparison\n• Booking a free demo\n\nWhat interests you most?";
    }

    addBotMessage(response);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send booking to backend
      const response = await axios.post(`${API}/bookings`, {
        name: bookingData.name,
        email: bookingData.email,
        company: bookingData.company,
        employees: bookingData.employees,
        source: 'zen1_chat'
      });
      
      // Add confirmation message
      const confirmMsg = {
        id: Date.now(),
        type: 'bot',
        text: `Perfect, ${bookingData.name}! 🎉\n\nI've noted your details:\n📧 ${bookingData.email}\n🏢 ${bookingData.company}\n👥 ${bookingData.employees} employees\n\nOur team will reach out within 2 hours to confirm your 7-minute demo slot.\n\nIs there anything else I can help you with?`,
        time: new Date()
      };
      
      setShowBookingForm(false);
      setMessages(prev => [...prev, confirmMsg]);
      
      // Reset form
      setBookingData({ name: '', email: '', company: '', employees: '50-100' });
      
    } catch (error) {
      console.error('Booking error:', error);
      
      // Fallback to email if API fails
      const subject = encodeURIComponent('Zenova Demo Request - 7 Min Session (via Zen1)');
      const body = encodeURIComponent(
        `Hi Zenova Team,\n\nI would like to book a 7-minute demo session.\n\nDetails:\n- Name: ${bookingData.name}\n- Company: ${bookingData.company}\n- Email: ${bookingData.email}\n- Company Size: ${bookingData.employees} employees\n\nPlease share available time slots.\n\nThank you!`
      );
      window.open(`mailto:info@zenovagrowthpartners.com?subject=${subject}&body=${body}`, '_blank');
      
      const confirmMsg = {
        id: Date.now(),
        type: 'bot',
        text: `Thanks ${bookingData.name}! I've opened your email client with the booking details. Please send the email to complete your booking request.\n\nIs there anything else I can help you with?`,
        time: new Date()
      };
      
      setShowBookingForm(false);
      setMessages(prev => [...prev, confirmMsg]);
      setBookingData({ name: '', email: '', company: '', employees: '50-100' });
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-16 h-16 rounded-full flex items-center justify-center shadow-2xl z-[9999] transition-all duration-300 hover:scale-110 group"
          style={{ background: 'var(--brand-dark)' }}
        >
          <div className="relative">
            <MessageCircle size={28} style={{ color: 'var(--brand-primary)' }} />
            {/* Notification dot */}
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
          </div>
          {/* Tooltip */}
          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-white rounded-xl shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            <p className="text-sm font-medium" style={{ color: 'var(--brand-dark)' }}>Chat with Zen1</p>
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed bottom-6 right-6 z-[9999] transition-all duration-300 ${
            isMinimized ? 'w-80' : 'w-[380px]'
          }`}
        >
          <div 
            className="rounded-3xl shadow-2xl overflow-hidden"
            style={{ background: 'white' }}
          >
            {/* Header */}
            <div 
              className="p-4 flex items-center justify-between"
              style={{ background: 'var(--brand-dark)' }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center relative"
                  style={{ background: 'var(--brand-primary)' }}
                >
                  <Bot size={24} style={{ color: 'var(--brand-dark)' }} />
                  <Sparkles size={12} className="absolute -top-1 -right-1" style={{ color: 'var(--brand-primary)' }} />
                </div>
                <div>
                  <h4 className="text-white font-bold text-base flex items-center gap-2">
                    Zen1
                    <span className="text-xs font-normal px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }}>
                      AI Assistant
                    </span>
                  </h4>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-white/70 text-xs">Always online</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
                >
                  <Minimize2 size={16} className="text-white/70" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
                >
                  <X size={16} className="text-white/70" />
                </button>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Messages */}
                <div 
                  className="h-80 overflow-y-auto p-4 space-y-4"
                  style={{ background: 'var(--bg-page)' }}
                >
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.type === 'bot' && (
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center mr-2 flex-shrink-0"
                          style={{ background: 'var(--brand-primary)' }}
                        >
                          <Bot size={16} style={{ color: 'var(--brand-dark)' }} />
                        </div>
                      )}
                      <div
                        className={`max-w-[75%] p-3 rounded-2xl ${
                          msg.type === 'user' 
                            ? 'rounded-br-sm' 
                            : 'rounded-bl-sm'
                        }`}
                        style={{ 
                          background: msg.type === 'user' ? 'var(--brand-dark)' : 'white',
                          color: msg.type === 'user' ? 'white' : 'var(--text-primary)',
                          boxShadow: msg.type === 'bot' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                        }}
                      >
                        <p className="text-sm whitespace-pre-line leading-relaxed">{msg.text}</p>
                      </div>
                      {msg.type === 'user' && (
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center ml-2 flex-shrink-0"
                          style={{ background: 'rgba(0, 69, 52, 0.1)' }}
                        >
                          <User size={16} style={{ color: 'var(--brand-dark)' }} />
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Typing indicator */}
                  {isTyping && (
                    <div className="flex justify-start">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center mr-2 flex-shrink-0"
                        style={{ background: 'var(--brand-primary)' }}
                      >
                        <Bot size={16} style={{ color: 'var(--brand-dark)' }} />
                      </div>
                      <div 
                        className="px-4 py-3 rounded-2xl rounded-bl-sm"
                        style={{ background: 'white' }}
                      >
                        <div className="flex gap-1">
                          <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Booking Form */}
                  {showBookingForm && (
                    <div 
                      className="p-4 rounded-2xl"
                      style={{ background: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Calendar size={18} style={{ color: 'var(--brand-dark)' }} />
                        <h4 className="font-semibold text-sm" style={{ color: 'var(--brand-dark)' }}>
                          Book Your 7-Min Demo
                        </h4>
                      </div>
                      <form onSubmit={handleBookingSubmit} className="space-y-3">
                        <input
                          type="text"
                          required
                          placeholder="Your Name"
                          value={bookingData.name}
                          onChange={(e) => setBookingData({...bookingData, name: e.target.value})}
                          className="w-full px-3 py-2 rounded-xl text-sm border focus:outline-none focus:ring-2"
                          style={{ borderColor: 'rgba(0,69,52,0.2)', background: 'var(--bg-page)' }}
                        />
                        <input
                          type="email"
                          required
                          placeholder="Work Email"
                          value={bookingData.email}
                          onChange={(e) => setBookingData({...bookingData, email: e.target.value})}
                          className="w-full px-3 py-2 rounded-xl text-sm border focus:outline-none focus:ring-2"
                          style={{ borderColor: 'rgba(0,69,52,0.2)', background: 'var(--bg-page)' }}
                        />
                        <input
                          type="text"
                          required
                          placeholder="Company Name"
                          value={bookingData.company}
                          onChange={(e) => setBookingData({...bookingData, company: e.target.value})}
                          className="w-full px-3 py-2 rounded-xl text-sm border focus:outline-none focus:ring-2"
                          style={{ borderColor: 'rgba(0,69,52,0.2)', background: 'var(--bg-page)' }}
                        />
                        <select
                          value={bookingData.employees}
                          onChange={(e) => setBookingData({...bookingData, employees: e.target.value})}
                          className="w-full px-3 py-2 rounded-xl text-sm border focus:outline-none"
                          style={{ borderColor: 'rgba(0,69,52,0.2)', background: 'var(--bg-page)' }}
                        >
                          <option value="50-100">50-100 employees</option>
                          <option value="100-200">100-200 employees</option>
                          <option value="200-300">200-300 employees</option>
                          <option value="300-500">300-500 employees</option>
                        </select>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setShowBookingForm(false)}
                            className="flex-1 px-3 py-2 rounded-xl text-sm font-medium transition-colors"
                            style={{ background: 'rgba(0,69,52,0.1)', color: 'var(--brand-dark)' }}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="flex-1 px-3 py-2 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02]"
                            style={{ background: 'var(--brand-dark)', color: 'white' }}
                          >
                            Book Now
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Quick Replies */}
                <div className="px-4 py-2 flex gap-2 overflow-x-auto" style={{ background: 'var(--bg-page)' }}>
                  {quickReplies.map((reply, index) => (
                    <button
                      key={index}
                      onClick={() => handleSend(reply)}
                      className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 hover:scale-105"
                      style={{ 
                        background: 'white',
                        color: 'var(--brand-dark)',
                        border: '1px solid rgba(0, 69, 52, 0.2)'
                      }}
                    >
                      {reply}
                    </button>
                  ))}
                </div>

                {/* Input */}
                <div className="p-4 border-t" style={{ borderColor: 'rgba(0, 69, 52, 0.1)' }}>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                      placeholder="Ask Zen1 anything..."
                      className="flex-grow px-4 py-2.5 rounded-xl text-sm focus:outline-none"
                      style={{ 
                        background: 'var(--bg-page)',
                        color: 'var(--text-primary)'
                      }}
                    />
                    <button
                      onClick={() => handleSend()}
                      disabled={!message.trim()}
                      className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105 disabled:opacity-50"
                      style={{ background: 'var(--brand-dark)' }}
                    >
                      <Send size={18} style={{ color: 'var(--brand-primary)' }} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default LiveChatWidget;
